"use client"

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react"
import { convertToLocale } from "@lib/util/money"
import { HttpTypes } from "@medusajs/types"
import { Button } from "@/components/common"
import { ShoppingBag } from "lucide-react"
import CartDrawer from "@modules/cart/components/cart-drawer"
import DeleteButton from "@modules/common/components/delete-button"
import LineItemPrice from "@modules/common/components/line-item-price"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "@modules/products/components/thumbnail"
import { usePathname } from "next/navigation"
import { useTranslations, useLocale } from "next-intl"
import { Fragment, useEffect, useRef, useState } from "react"
import { useCart } from "@/hooks/use-cart"
import { getLocalizedItemTitle } from "@lib/util/cart-localization"

const CartDropdown = ({
  cart: cartState,
}: {
  cart?: HttpTypes.StoreCart | null
}) => {
  const t = useTranslations('common')
  const tCart = useTranslations('cart.dropdown')
  const locale = useLocale()
  const intlLocaleMap: Record<string, string> = {
    ru: "ru-RU", en: "en-US", cs: "cs-CZ", uk: "uk-UA",
  }
  const { cart: reactiveCart } = useCart(cartState)
  const cart = (reactiveCart ?? cartState ?? null) as HttpTypes.StoreCart | null
  const [activeTimer, setActiveTimer] = useState<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )
  const [cartDropdownOpen, setCartDropdownOpen] = useState(false)

  const open = () => setCartDropdownOpen(true)
  const close = () => setCartDropdownOpen(false)

  // Hide service line items (return shipping, etc.) from the widget —
  // they belong to checkout summary, not the user-facing list.
  const visibleItems = (cart?.items || []).filter((item) => {
    const meta = (item.metadata as any) || {}
    return !meta.isReturnShipping && !meta.isServiceItem
  })
  const totalItems = visibleItems.reduce((acc, item) => acc + item.quantity, 0)
  // Items-only total (no return-shipping line). cart.item_total still
  // includes service line items because Medusa doesn't filter them out;
  // sum the visible ones ourselves.
  const itemTotal = visibleItems.reduce((acc, item) => acc + (item.total ?? 0), 0)
  const itemRef = useRef<number>(totalItems || 0)
  const [badgeBounce, setBadgeBounce] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const timedOpen = () => {
    open()

    const timer = setTimeout(close, 5000)

    setActiveTimer(timer)
  }

  const openAndCancel = () => {
    if (activeTimer) {
      clearTimeout(activeTimer)
    }

    open()
  }

  // Clean up the timer when the component unmounts
  useEffect(() => {
    return () => {
      if (activeTimer) {
        clearTimeout(activeTimer)
      }
    }
  }, [activeTimer])

  const pathname = usePathname()

  // open cart dropdown when modifying the cart items, but only if we're not on the cart page
  useEffect(() => {
    if (itemRef.current !== totalItems && !pathname.includes("/cart")) {
      timedOpen()
      // Trigger badge bounce animation
      setBadgeBounce(true)
      const timer = setTimeout(() => setBadgeBounce(false), 300)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalItems, itemRef.current])

  return (
    <div
      className="h-full z-50"
      onMouseEnter={openAndCancel}
      onMouseLeave={close}
    >
      {/* Screen reader announcement for cart updates */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {tCart('cart', { totalItems })}
      </div>
      <Popover className="relative h-full hidden small:block">
        <PopoverButton
          className="h-full"
          aria-label={`${tCart('cart', { totalItems })} - ${totalItems} items`}
        >
          <LocalizedClientLink
            className="hover:text-ui-fg-base relative"
            href="/cart"
            data-testid="nav-cart-link"
          >
            <ShoppingBag className="w-5 h-5" />
            {totalItems > 0 && (
              <span
                className={`absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 text-[10px] font-medium rounded-full bg-primary text-primary-foreground transition-transform duration-300 ${badgeBounce ? 'scale-125' : 'scale-100'}`}
                data-cart-count={totalItems}
                aria-hidden="true"
              >
                {totalItems}
              </span>
            )}
          </LocalizedClientLink>
        </PopoverButton>
        <Transition
          show={cartDropdownOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <PopoverPanel
            static
            className="hidden small:block absolute top-[calc(100%+1px)] right-0 bg-popover border border-border rounded-b-lg shadow-lg w-[380px] text-popover-foreground"
            data-testid="nav-cart-dropdown"
          >
            <div className="px-5 py-3 border-b border-border">
              <h3 className="text-sm font-semibold">{tCart('cart', { totalItems })}</h3>
            </div>
            {cart && visibleItems.length ? (
              <>
                <div className="overflow-y-auto max-h-[360px] divide-y divide-border">
                  {visibleItems
                    .sort((a, b) => {
                      return (a.created_at ?? "") > (b.created_at ?? "")
                        ? -1
                        : 1
                    })
                    .map((item) => (
                      <div
                        className="flex gap-3 px-5 py-4"
                        key={item.id}
                        data-testid="cart-item"
                      >
                        <LocalizedClientLink
                          href={`/products/${item.product_handle}`}
                          className="shrink-0 w-16 h-16"
                        >
                          <Thumbnail
                            thumbnail={item.thumbnail || item.variant?.product?.thumbnail}
                            images={item.variant?.product?.images}
                            size="square"
                          />
                        </LocalizedClientLink>
                        <div className="flex flex-col flex-1 min-w-0 justify-between">
                          <div className="flex items-start justify-between gap-x-2">
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-medium leading-tight truncate">
                                <LocalizedClientLink
                                  href={`/products/${item.product_handle}`}
                                  className="hover:underline"
                                  data-testid="product-link"
                                >
                                  {getLocalizedItemTitle(item, locale)}
                                </LocalizedClientLink>
                              </h3>
                              <span
                                className="text-xs text-muted-foreground"
                                data-testid="cart-item-quantity"
                                data-value={item.quantity}
                              >
                                {tCart('quantity', { quantity: item.quantity })}
                              </span>
                            </div>
                            <div className="shrink-0 text-sm font-medium">
                              <LineItemPrice
                                item={item}
                                style="tight"
                                currencyCode={cart.currency_code}
                              />
                            </div>
                          </div>
                          <DeleteButton
                            id={item.id}
                            className="self-start text-xs"
                            data-testid="cart-item-remove-button"
                          >
                            {tCart('remove')}
                          </DeleteButton>
                        </div>
                      </div>
                    ))}
                </div>
                <div className="px-5 py-4 border-t border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {tCart('subtotal')}
                    </span>
                    <span
                      className="text-sm font-semibold"
                      data-testid="cart-subtotal"
                      data-value={itemTotal}
                    >
                      {convertToLocale({
                        amount: itemTotal,
                        currency_code: cart.currency_code,
                      })}
                    </span>
                  </div>
                  <Button
                    asChild
                    className="w-full"
                    size="large"
                    data-testid="go-to-cart-button"
                  >
                    <LocalizedClientLink href="/cart">
                      {tCart('goToCart')}
                    </LocalizedClientLink>
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex py-12 flex-col gap-y-3 items-center justify-center px-5">
                <div className="bg-muted text-sm flex items-center justify-center w-8 h-8 rounded-full text-muted-foreground">
                  0
                </div>
                <span className="text-sm text-muted-foreground">{tCart('emptyBag')}</span>
                <Button asChild size="small" onClick={close}>
                  <LocalizedClientLink href="/store">
                    {tCart('explore')}
                  </LocalizedClientLink>
                </Button>
              </div>
            )}
          </PopoverPanel>
        </Transition>
      </Popover>

      {/* Mobile: Cart Drawer (slide-over) */}
      <Button
        variant="ghost"
        className="h-auto p-0 rounded-none font-normal small:hidden h-full"
        onClick={() => setDrawerOpen(true)}
        aria-label={`${tCart('cart', { totalItems })} - ${totalItems} items`}
      >
        <span className="relative hover:text-ui-fg-base">
          <ShoppingBag className="w-5 h-5" />
          {totalItems > 0 && (
            <span
              className={`absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 text-[10px] font-medium rounded-full bg-primary text-primary-foreground transition-transform duration-300 ${badgeBounce ? 'scale-125' : 'scale-100'}`}
              data-cart-count={totalItems}
              aria-hidden="true"
            >
              {totalItems}
            </span>
          )}
        </span>
      </Button>
      <CartDrawer
        cart={cart}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  )
}

export default CartDropdown
